import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Sparkles,
  Eye,
  EyeOff,
  Loader2,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Signup = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [orgName, setOrgName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup`, {
        org_name: orgName.trim(),
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
      });
      const { token, user } = res.data;
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Push into AuthContext via a fresh login state
      // Easiest path: full reload with the token already saved
      toast.success(`Welcome to ${orgName.trim()}!`);
      // Reload to let AuthProvider pick up the token and route the new admin to /dashboard
      window.location.href = '/dashboard';
      return user;
    } catch (err) {
      const msg =
        err.response?.data?.detail || err.message || 'Signup failed. Please try again.';
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100 flex"
      data-testid="signup-page"
    >
      {/* Left — value props */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-10 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 border-r border-slate-800 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

        <Link
          to="/"
          className="relative flex items-center gap-2 z-10"
          data-testid="signup-logo-home"
        >
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-slate-900" />
          </div>
          <span
            className="text-lg font-semibold"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            InterviewWala
          </span>
        </Link>

        <div className="relative z-10 max-w-md">
          <h1
            className="text-4xl font-bold leading-tight mb-4"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Spin up your<br />
            <span className="bg-gradient-to-r from-teal-300 to-emerald-400 bg-clip-text text-transparent">
              hiring workspace
            </span>
            <br />in 2 minutes.
          </h1>
          <p className="text-slate-300 mb-8 leading-relaxed">
            Free forever for small teams. AI-powered candidate matching, bulk resume uploads, and
            a single-screen Recruiter Command Center — all yours after this form.
          </p>
          <ul className="space-y-3 text-sm text-slate-300">
            {[
              'No credit card required',
              'Your data isolated to your organisation',
              'Invite your team in seconds',
              'Cancel anytime — your data exports cleanly',
            ].map((b) => (
              <li key={b} className="flex items-center gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-teal-400" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 text-xs text-slate-500">
          © 2026 InterviewWala ATS
        </p>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-slate-900" />
            </div>
            <span
              className="text-lg font-semibold"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              InterviewWala
            </span>
          </div>
          <h2
            className="text-3xl font-bold mb-1"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Create your workspace
          </h2>
          <p className="text-sm text-slate-400 mb-7">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-teal-400 hover:underline"
              data-testid="signup-link-login"
            >
              Sign in
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-3.5">
            <div>
              <Label
                htmlFor="org_name"
                className="text-xs text-slate-300 font-medium"
              >
                Company / staffing firm name
              </Label>
              <Input
                id="org_name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                minLength={2}
                placeholder="Acme Staffing"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
                data-testid="signup-org-name-input"
              />
            </div>
            <div>
              <Label
                htmlFor="name"
                className="text-xs text-slate-300 font-medium"
              >
                Your full name
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Jane Doe"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <Label
                htmlFor="email"
                className="text-xs text-slate-300 font-medium"
              >
                Work email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="jane@acme.com"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500"
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <Label
                htmlFor="password"
                className="text-xs text-slate-300 font-medium"
              >
                Password
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="At least 6 characters"
                  className="bg-slate-900 border-slate-700 text-slate-100 placeholder:text-slate-500 pr-10"
                  data-testid="signup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  aria-label="Toggle password"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-teal-400 to-emerald-500 text-slate-900 hover:opacity-90 font-semibold"
              data-testid="signup-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Create workspace
            </Button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-6">
            By creating an account, you agree to our terms of service and privacy policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
